import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowDown } from "lucide-react";
import { Asterisk } from "./Brand";
import { IMAGES, EPISODES, isSpecialEpisode } from "@/data/content";
import { maskLine, scrollTo } from "@/lib/motion";

const line = (delay) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 0.95, ease: [0.215, 0.61, 0.355, 1], delay },
});

export default function HeroSection({ latest, onPlay }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.35]);
  const ep = latest || EPISODES[0];

  return (
    <section className="relative flex min-h-screen items-end overflow-hidden pb-16 pt-36 lg:items-center lg:pb-0">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <motion.img
          src={IMAGES.heroSkyline}
          alt="Los Angeles skyline at night with pink light hues, seen from the hills"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 2.4, ease: [0.215, 0.61, 0.355, 1] }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="hero-badge"
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-cherry/40 bg-cherry/10 px-4 py-1.5"
          >
            <span className="h-2 w-2 rounded-full bg-cherry animate-pulse-dot" />
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-cherry">
              CGD SZN 3 — Out Now
            </span>
          </motion.div>

          <h1 data-testid="hero-title" className="font-display font-extrabold uppercase leading-[0.95] tracking-tight">
            <span className="block overflow-hidden text-[16vw] sm:text-7xl lg:text-8xl xl:text-[6.5rem]">
              <motion.span {...line(0.2)} className="block">
                City Girls
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                {...line(0.38)}
                className="block font-garamond text-[17vw] font-medium normal-case italic leading-[1.02] text-cherry sm:text-7xl lg:text-8xl xl:text-[6.5rem]"
              >
                Decoded
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed sm:text-lg"
          >
            <span className="block font-semibold text-white">
              We say the shit everybody else waits until the group chat to say.
            </span>
            <span className="mt-3 block text-white/60">
              Love. Dating. Culture. Mess. Healing. Bad decisions. Better
              decisions. And the stories that come with all of it — out of the
              group chat and into real life, with a mic, a camera, and
              absolutely no reason to keep it cute.
            </span>
            <span className="mt-4 block text-[11px] font-bold uppercase tracking-[0.22em] text-cherry">
              Hosted by Morgan Chanel
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-play-btn"
              onClick={() => onPlay(ep)}
              className="group inline-flex items-center gap-3 rounded-full bg-cherry px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:scale-[1.04] hover:bg-cherry-soft hover:shadow-[0_0_40px_rgba(255,0,85,0.45)]"
            >
              <Play className="h-4 w-4 fill-current" />
              Play latest episode
            </button>
            <button
              data-testid="hero-subscribe-cta"
              onClick={() => scrollTo("#platforms")}
              className="inline-flex items-center gap-3 rounded-full border border-white/25 px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white/85 transition-colors duration-300 hover:border-cherry hover:text-cherry"
            >
              Subscribe
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-14 hidden max-w-md lg:mt-20 lg:block"
        >
          <div className="glass flex items-center gap-5 rounded-2xl border border-white/10 p-5">
            <img
              src={ep?.art_url || IMAGES.cover}
              alt={ep?.title ? `${ep.title} art` : "City Girls Decoded cover art"}
              className="h-16 w-16 shrink-0 rounded-xl object-cover"
            />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cherry">
                {isSpecialEpisode(ep) ? "CGD: IRL Special" : `Episode ${ep.number}`} · {ep.released}
              </p>
              <p className="mt-1 truncate font-display text-sm font-bold">{ep.title}</p>
            </div>
            <button
              data-testid="hero-card-play-btn"
              onClick={() => onPlay(ep)}
              className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cherry text-white transition-transform hover:scale-110"
              aria-label="Play episode preview"
            >
              <Play className="h-4 w-4 fill-current" />
            </button>
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute bottom-8 right-6 hidden items-center gap-3 text-white/50 lg:flex">
        <Asterisk className="h-4 w-4 animate-spin-slow text-cherry" />
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Watch. Listen. Get into it.</span>
      </div>
    </section>
  );
}
